import { useStore } from '@nanostores/react';
import * as RadixDialog from '@radix-ui/react-dialog';
import * as RadixSelect from '@radix-ui/react-select';
import { memo, useEffect, useState, useRef, useCallback, type ReactNode } from 'react';
import { toast } from 'react-toastify';
import { Alert } from '~/components/ui/Alert';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { Dialog } from '~/components/ui/LegacyDialog';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { Link } from '~/components/ui/Link';
import { LoadingBar } from '~/components/ui/LoadingBar';
import * as Tooltip from '~/components/ui/Tooltip';
import { getOrganizations } from '~/lib/.client/api/supabase';
import { pauseProject, restoreProject, getOrganizationInfo, getProjectStatus } from '~/lib/.client/api/supabase';
import { connectToSupabase, reconnectSupabase } from '~/lib/.client/supabase';
import { availableRegions, mapStatusToDisplayStatus } from '~/lib/data/supabase';
import { supabaseStore, type SupabaseProjectConfigurationSteps } from '~/lib/stores/supabase';
import { themeStore } from '~/lib/stores/theme';
import type { SupabaseOrganizationWithProjects, SupabaseProject, SupabaseProjectStatus } from '~/types/supabase';
import { classNames } from '~/utils/classNames';
import { logger } from '~/utils/logger';
import { generateStrongPassword } from '~/utils/password';
import { wait } from '~/utils/promises';
import { getClosestRegion, sortProjectsByStatus } from '~/utils/supabase';

interface SelectProjectProps {
  handleSelectProject: (project: any) => void;
  handleCreateNewProject: VoidFunction;
  updateProjectStatus: (project: SupabaseProject) => void;
  pollProjectStatus: (project: SupabaseProject, endStatus: SupabaseProjectStatus) => void;
  organization: SupabaseOrganizationWithProjects | null;
  isLoading: boolean;
  isFreePlan: boolean;
}

const projectTitleClassNames = 'text-bolt-elements-textPrimary text-sm leading-6 mb-3';
const widthClassName = 'w-[548px]';
const projectItemClassName = `h-16 w-full items-center justify-between rounded-md border border-bolt-elements-borderColor px-4 py-3 group`;

const FREE_PLAN_PROJECT_LIMIT = 2;
const PASSWORD_MIN_LENGTH = 4;
const PASSWORD_RECOMMENDED_LENGTH = 12;
const PENDING_PROJECT_STATUS = ['PAUSING', 'COMING_UP', 'RESTORING'];
const ERROR_PROJECT_STATUS = ['RESTORE_FAILED', 'PAUSE_FAILED'];

function handleCreateNewProject() {
  supabaseStore.supabaseProjectConfigurationStep.set('create-new');
}

function handleReturnToSelectProject() {
  supabaseStore.supabaseProjectConfigurationStep.set('initial');
}

function onClose(step: SupabaseProjectConfigurationSteps) {
  supabaseStore.showSupabaseProjectConfiguration.set(false);
  supabaseStore.supabaseProjectConfigurationStep.set('initial');

  if (step === 'confirm-creation') {
    supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
    supabaseStore.requiresConfirmation.set('declined');
  }
}

function onConfirm() {
  supabaseStore.showSupabaseProjectConfiguration.set(false);
  supabaseStore.supabaseProjectConfigurationStep.set('initial');
  supabaseStore.requiresConfirmation.set('accepted');
}

function SelectProject({
  organization,
  isLoading,
  isFreePlan,
  handleSelectProject,
  handleCreateNewProject,
  updateProjectStatus,
  pollProjectStatus,
}: SelectProjectProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProjects = organization
    ? organization.projects
        .filter((project) => project.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .sort(sortProjectsByStatus)
    : [];

  const isFreeProjectLimitsReached =
    isFreePlan &&
    (
      organization?.projects.filter((project) =>
        ['ACTIVE_HEALTHY', ...PENDING_PROJECT_STATUS].includes(project.status),
      ) ?? []
    ).length === FREE_PLAN_PROJECT_LIMIT;

  async function onPauseProject(e: React.MouseEvent<HTMLButtonElement>, project: SupabaseProject) {
    e.stopPropagation();

    const originalStatus = project.status;

    try {
      updateProjectStatus({ ...project, status: 'PAUSING' });

      await pauseProject(project.id);

      pollProjectStatus(project, 'INACTIVE');
    } catch (error) {
      updateProjectStatus({ ...project, status: originalStatus });

      toast.error('Failed to pause the project');

      logger.error(error);
    }
  }

  async function onRestoreProject(e: React.MouseEvent<HTMLButtonElement>, project: SupabaseProject) {
    e.stopPropagation();

    const originalStatus = project.status;

    try {
      updateProjectStatus({ ...project, status: 'COMING_UP' });

      await restoreProject(project.id, () => {
        supabaseStore.showProjectLimitReached.set(true);
      });

      pollProjectStatus(project, 'ACTIVE_HEALTHY');
    } catch (error) {
      updateProjectStatus({ ...project, status: originalStatus });

      toast.error('Failed to restore the project');

      logger.error(error);
    }
  }

  return (
    <div className="min-h-[330px] flex flex-col justify-between">
      <DialogHeader
        title="Select your database"
        description="Connect with a Supabase project to manage your data, set up authentication, create backend functions, and more."
      />
      <>
        <div className={`${widthClassName} text-sm mb-3`}>
          <div className={projectTitleClassNames}>
            Select a project
            {organization && organization.projects.length > 0 && (
              <>
                <span className="text-bolt-elements-textSecondary mx-2">/</span>
                <span className="text-bolt-elements-textTertiary">{organization?.name}</span>
              </>
            )}
          </div>

          {organization && organization.projects.length > 0 && (
            <input
              type="text"
              placeholder="Search for a project"
              className="w-full px-3 py-2 mb-1.5 rounded-md border border-bolt-elements-borderColor bg-transparent placeholder:text-bolt-elements-textTertiary"
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
              disabled={isLoading}
            />
          )}

          {isLoading && (
            <OverflowBottomGradient>
              <div className="max-h-40 overflow-hidden pb-4">
                {[1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className={`${projectItemClassName} mb-2 last:mb-0 h-100% shimmer bg-gray-100 dark:bg-gray-800`}
                  />
                ))}
              </div>
            </OverflowBottomGradient>
          )}

          {!isLoading && filteredProjects.length === 0 && (
            <div className="flex justify-center items-center text-bolt-elements-textSecondary mt-10 mb-12">
              <div>No projects found</div>
            </div>
          )}

          {!isLoading && filteredProjects.length > 0 && (
            <OverflowBottomGradient>
              <ul className="flex flex-col gap-2 max-h-40 overflow-y-auto no-scrollbar pb-4">
                {filteredProjects.map((project) => (
                  <Tooltip.Provider key={project.id}>
                    <Tooltip.Root delayDuration={300}>
                      <Tooltip.Trigger asChild>
                        <li
                          role="button"
                          className={classNames(
                            projectItemClassName,
                            'shrink-0 relative overflow-hidden transition-[height] ease-in-out duration-300',
                            {
                              'h-25':
                                PENDING_PROJECT_STATUS.includes(project.status) ||
                                ERROR_PROJECT_STATUS.includes(project.status),
                              'border-bolt-ds-brand': project.status === 'ACTIVE_HEALTHY',
                              'bg-bolt-ds-warning-container': ERROR_PROJECT_STATUS.includes(project.status),
                              'bg-[rgba(20,136,252,0.10)]': project.status === 'ACTIVE_HEALTHY',
                              'cursor-default': project.status === 'INACTIVE',
                            },
                          )}
                          onClick={() => handleSelectProject(project)}
                        >
                          {PENDING_PROJECT_STATUS.includes(project.status) && <LoadingBar />}
                          <div
                            className={classNames('flex w-full items-center justify-between', {
                              'text-bolt-elements-textSecondary':
                                PENDING_PROJECT_STATUS.includes(project.status) ||
                                ERROR_PROJECT_STATUS.includes(project.status) ||
                                project.status === 'INACTIVE',
                            })}
                          >
                            <div className="text-sm text-left">
                              <div className="w-80">
                                <div className="mb-1 truncate">{project.name}</div>
                              </div>
                              <div className="text-xs text-bolt-elements-textSecondary">
                                {project.region} - {mapStatusToDisplayStatus[project.status]}
                              </div>
                            </div>
                            <div
                              className={classNames('flex items-center gap-3 group', {
                                'sr-only': project.status === 'ACTIVE_HEALTHY',
                                'group-hover:not-sr-only': project.status === 'ACTIVE_HEALTHY',
                              })}
                            >
                              {project.status === 'ACTIVE_HEALTHY' && (
                                <>
                                  <LegacyIconButton
                                    icon="i-ph:pause-fill text-bolt-elements-textPrimary"
                                    size="sm"
                                    className="p-3"
                                    title="Pause project"
                                    onClick={(e) => onPauseProject(e, project)}
                                  />
                                  <div className="i-ph:caret-right text-xs group-hover:visible" />
                                </>
                              )}
                              {project.status === 'INACTIVE' && (
                                <>
                                  <LegacyIconButton
                                    icon="i-ph:play-fill text-bolt-elements-textPrimary"
                                    size="sm"
                                    className="p-3"
                                    title="Restore project"
                                    disabled={isFreeProjectLimitsReached}
                                    onClick={(e) => onRestoreProject(e, project)}
                                  />
                                  <div className="i-ph:caret-right text-xs" />
                                </>
                              )}
                            </div>
                          </div>
                          {PENDING_PROJECT_STATUS.includes(project.status) && (
                            <div className="border-t border-bolt-elements-borderColor pt-2 mt-3">
                              <p className="text-bolt-elements-textTertiary text-xs">
                                {project.status === 'PAUSING' ? 'Pausing' : 'Activating'} project might take several
                                minutes, please wait.
                              </p>
                            </div>
                          )}
                          {ERROR_PROJECT_STATUS.includes(project.status) && (
                            <div className="border-t border-bolt-elements-borderColor pt-2 mt-3">
                              <p className="text-bolt-elements-textTertiary text-xs">
                                Something went wrong, please check your project in{' '}
                                <a
                                  href={`https://supabase.com/dashboard/project/${project.id}`}
                                  target="_blank"
                                  className="underline hover:text-bolt-elements-textPrimary"
                                >
                                  Supabase
                                </a>
                                .
                              </p>
                            </div>
                          )}
                        </li>
                      </Tooltip.Trigger>
                      {project.status === 'INACTIVE' && (
                        <Tooltip.Portal>
                          <Tooltip.Content side="bottom" align="center" sideOffset={6}>
                            You need to restore the project in order to connect it.
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      )}
                    </Tooltip.Root>
                  </Tooltip.Provider>
                ))}
              </ul>
            </OverflowBottomGradient>
          )}
        </div>
        {isFreeProjectLimitsReached && (
          <Alert title="You’ve reached your active project limit." type="warning" className="mb-3">
            In order to create a new project, you need to{' '}
            <a
              href={`https://supabase.com/dashboard/org/${organization?.id}/billing`}
              target="_blank"
              className="underline hover:text-bolt-elements-textPrimary"
            >
              upgrade your Supabase plan
            </a>{' '}
            or pause some of your projects.
          </Alert>
        )}
      </>
      <div className="h-11">
        {organization && organization.projects.length > 0 && (
          <div className="text-center text-bolt-elements-textSecondary">or</div>
        )}
      </div>

      <div className="flex flex-col items-center gap-2">
        <LegacyButton
          variant="secondary"
          className="h-10 w-fit"
          onClick={handleCreateNewProject}
          disabled={isFreeProjectLimitsReached}
        >
          <span className="i-logos:supabase-icon size-4 mr-2" />
          Create a new project
        </LegacyButton>
        {organization && <div className="text-xs text-bolt-elements-textTertiary mb-3">in {organization?.name}</div>}
      </div>
    </div>
  );
}

interface CreateNewProjectProps {
  closeDialog: VoidFunction;
  organization: SupabaseOrganizationWithProjects | null;
}

const labelClassNames = 'text-bolt-elements-textSecondary font-medium mb-2 block';

const mapCountryCodeToFlagClassName = {
  us: 'i-bolt:country-flags-us',
  ca: 'i-bolt:country-flags-ca',
  ie: 'i-bolt:country-flags-ie',
  gb: 'i-bolt:country-flags-gb',
  fr: 'i-bolt:country-flags-fr',
  de: 'i-bolt:country-flags-de',
  ch: 'i-bolt:country-flags-ch',
  se: 'i-bolt:country-flags-se',
  in: 'i-bolt:country-flags-in',
  sg: 'i-bolt:country-flags-sg',
  jp: 'i-bolt:country-flags-jp',
  kr: 'i-bolt:country-flags-kr',
  au: 'i-bolt:country-flags-au',
  br: 'i-bolt:country-flags-br',
};

const closestRegion = getClosestRegion();

function CreateNewProject({ closeDialog, organization }: CreateNewProjectProps) {
  const [projectName, setProjectName] = useState('');
  const [projectPassword, setProjectPassword] = useState('');
  const [projectRegion, setProjectRegion] = useState(closestRegion);
  const [showCopied, setShowCopied] = useState(false);

  const [passwordStrengthInfo, setPasswordStrengthInfo] = useState<{
    color: string;
    strength: number;
  }>({
    color: 'bg-red-500',
    strength: 0,
  });

  const supabaseStatus = useStore(supabaseStore.supabaseStatus);
  const connecting = supabaseStatus === 'CONNECTING';

  const onCreateNewProject = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    await connectToSupabase(false, false, { name: projectName, password: projectPassword, region: projectRegion.id });

    const confirmationNeeded = supabaseStore.requiresConfirmation.get();

    if (confirmationNeeded === 'pending') {
      supabaseStore.supabaseProjectConfigurationStep.set('confirm-creation');
    } else {
      closeDialog();
    }
  };

  function onChangePassword(value: string) {
    setProjectPassword(value);

    let strength = 0;

    if (value.length >= PASSWORD_MIN_LENGTH) {
      strength += 20;

      strength += Math.min(30, (value.length / PASSWORD_RECOMMENDED_LENGTH) * 30);
    }

    if (/[A-Z]/.test(value)) {
      strength += 10;
    }

    if (/[a-z]/.test(value)) {
      strength += 10;
    }

    if (/[0-9]/.test(value)) {
      strength += 15;
    }

    if (/[^A-Za-z0-9]/.test(value)) {
      strength += 15;
    }

    setPasswordStrengthInfo({
      color: getStrengthColor(strength),
      strength,
    });
  }

  const getStrengthColor = (strength: number) => {
    if (strength < 30) {
      return 'bg-red-500';
    }

    if (strength < 60) {
      return 'bg-yellow-500';
    }

    if (strength < 80) {
      return 'bg-blue-500';
    }

    return 'bg-green-500';
  };

  const copyPasswordToClipboard = () => {
    if (projectPassword) {
      navigator.clipboard
        .writeText(projectPassword)
        .then(() => {
          setShowCopied(true);

          setTimeout(() => setShowCopied(false), 2000);
        })
        .catch(() => {
          toast.error('Failed to copy password');
        });
    }
  };

  return (
    <>
      <DialogHeader title="Create your database" description="Configure your database information." />
      <form className={`${widthClassName} flex flex-col gap-4 text-sm`} onSubmit={onCreateNewProject}>
        <div>
          <label htmlFor="project-name" className={labelClassNames}>
            Project name
          </label>
          <input
            type="text"
            id="project-name"
            placeholder="My project name"
            value={projectName}
            required
            onChange={(e) => setProjectName(e.target.value)}
            className="block w-full px-3 py-2 mb-1.5 rounded-md border border-bolt-elements-borderColor"
          />
        </div>
        <div>
          <label htmlFor="project-password" className={labelClassNames}>
            Database password
          </label>
          <div className="relative">
            <input
              type="password"
              id="project-password"
              placeholder="Password"
              required
              value={projectPassword}
              onChange={(e) => onChangePassword(e.target.value)}
              className="block w-full px-3 py-2 mb-1.5 rounded-md border border-bolt-elements-borderColor pr-20"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
              {projectPassword && (
                <button
                  type="button"
                  onClick={copyPasswordToClipboard}
                  className="text-bolt-elements-textSecondary hover:text-bolt-elements-textPrimary p-1 flex gap-1 items-center bg-transparent"
                  title="Copy password"
                  hidden={!projectPassword}
                >
                  <div
                    className={classNames('text-lg', {
                      'i-ph:check': showCopied,
                      'i-ph:copy': !showCopied,
                    })}
                  ></div>
                  {showCopied ? 'Copied!' : 'Copy'}
                </button>
              )}
            </div>
          </div>

          <div
            className={classNames(
              'h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden opacity-100 transition-opacity duration-300 mb-2',
              {
                'opacity-30': !projectPassword,
              },
            )}
          >
            <div
              className={`h-full ${passwordStrengthInfo.color} transition-all duration-300`}
              style={{ width: `${passwordStrengthInfo.strength}%` }}
            />
          </div>

          <p className="text-bolt-elements-textTertiary">
            This is the password of your Postgres database, so it must be strong and hard to guess.{' '}
            <button
              className="bg-transparent text-bolt-elements-textSecondary hover:text-bolt-elements-textPrimary underline mr-1"
              onClick={(e) => {
                e.preventDefault();

                const generatedPassword = generateStrongPassword();
                onChangePassword(generatedPassword);
              }}
            >
              Generate a password
            </button>
          </p>
        </div>
        <div>
          <label htmlFor="project-region" className={labelClassNames}>
            Region
          </label>
          <RadixSelect.Root
            value={projectRegion.id}
            onValueChange={(value: string) => {
              const region = availableRegions.find((region) => region.id === value);

              if (region) {
                setProjectRegion(region);
              }
            }}
          >
            <RadixSelect.Trigger className="w-full px-3 py-2 mb-1.5 rounded-md border border-bolt-elements-borderColor bg-transparent flex justify-between">
              <RadixSelect.Value>
                <div className="flex items-center">
                  <span
                    className={`${mapCountryCodeToFlagClassName[projectRegion.countryCode]} inline-block size-4 mr-2`}
                  />
                  <span>{projectRegion.name}</span>
                </div>
              </RadixSelect.Value>
              <RadixSelect.Icon>
                <span className="inline-block i-ph:caret-down size-4 text-bolt-elements-textSecondary" />
              </RadixSelect.Icon>
            </RadixSelect.Trigger>
            <RadixSelect.Portal>
              <RadixSelect.Content
                position="popper"
                className={`${widthClassName} z-dialog h-70 min-w-full bg-bolt-elements-background-depth-1 rounded-lg`}
                sideOffset={4}
              >
                <RadixSelect.Viewport className="p-1 space-y-1">
                  {availableRegions.map((region) => (
                    <RadixSelect.Item
                      key={region.id}
                      value={region.id}
                      className="flex items-center pa-2 text-sm cursor-pointer hover:bg-bolt-elements-item-backgroundActive text-bolt-elements-textSecondary rounded-lg "
                    >
                      <span
                        className={`${mapCountryCodeToFlagClassName[region.countryCode]} inline-block size-4 mr-2`}
                      />
                      <RadixSelect.ItemText>
                        <span>{region.name}</span>
                      </RadixSelect.ItemText>
                    </RadixSelect.Item>
                  ))}
                </RadixSelect.Viewport>
              </RadixSelect.Content>
            </RadixSelect.Portal>
          </RadixSelect.Root>
          <p className="text-bolt-elements-textTertiary">
            Select the region closest to your users for the best performance.
          </p>
        </div>
        <div className="flex flex-col items-center gap-2 ">
          <LegacyButton type="submit" variant="secondary" className="h-10" disabled={connecting}>
            {connecting ? (
              <span className="i-svg-spinners:90-ring-with-bg text-emerald-600 dark:text-emerald-400 size-4 mr-2" />
            ) : (
              <span className="i-logos:supabase-icon size-4 mr-2" />
            )}
            Create a new project
          </LegacyButton>
          {organization && <div className="text-xs text-bolt-elements-textTertiary">in {organization?.name}</div>}
        </div>
      </form>
    </>
  );
}

interface ConfirmDatabaseCreationProps {
  organization: SupabaseOrganizationWithProjects | null;
}

function ConfirmDatabaseCreation({ organization }: ConfirmDatabaseCreationProps) {
  return (
    <>
      <DialogHeader title="Create your database" description="Confirmation needed" />
      <p className="mb-3">
        The first database on your paid Supabase plan comes at no additional costs. Launching additional databases
        starts at ~$10 per month per database. Additional fees for overages may apply.{' '}
        <Link href="https://supabase.com/pricing" target="_blank">
          <span className="i-ph:arrow-square-out inline-block align-mid relative -top-px mr-1" />
          Learn more
        </Link>
      </p>
      <div className="flex flex-col items-center gap-2 ">
        <LegacyButton onClick={onConfirm} variant="secondary" className="h-10">
          <span className="i-logos:supabase-icon size-4 mr-2" />
          Let's go!
        </LegacyButton>
        {organization && <div className="text-xs text-bolt-elements-textTertiary">in {organization?.name}</div>}
      </div>
    </>
  );
}

interface ConnectExistingProjectProps {
  closeDialog: VoidFunction;
  project: SupabaseProject;
}

function ConnectExistingProject({ closeDialog, project }: ConnectExistingProjectProps) {
  function connectProject() {
    supabaseStore.supabaseStatus.set('CONNECTING_TO_EXISTING_DATABASE');
    connectToSupabase(false, false, { supabaseProjectId: project.id });

    closeDialog();

    supabaseStore.supabaseProjectConfigurationStep.set('initial');
  }

  return (
    <>
      <DialogHeader
        title={`Connect ${project.name} to Bolt`}
        description="You're about to connect this project to Bolt. You can check project details before proceeding."
      />
      <div className={projectTitleClassNames}>
        Selected project<span className="text-bolt-elements-textSecondary mx-2">/</span>
        <span className="text-bolt-elements-textTertiary">{project.name}</span>
      </div>
      <button
        className="flex w-full items-center justify-between bg-transparent rounded-md border border-bolt-elements-borderColor hover:bg-bolt-elements-background-depth-4 px-4 py-3 mb-7"
        onClick={() => window.open(`https://supabase.com/dashboard/project/${project.id}`, '_blank')}
      >
        <div className="text-sm text-left w-80">
          <div className="mb-1 truncate">{project.name}</div>
          <div className="text-xs text-bolt-elements-textSecondary">{project.region}</div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="i-ph:arrow-up-right size-4" />
          View in Supabase
        </div>
      </button>
      <div className="flex flex-col items-center">
        <LegacyButton variant="secondary" className="h-10 mb-4 w-fit" onClick={connectProject}>
          Connect project
        </LegacyButton>
      </div>
    </>
  );
}

interface SupabaseConfigurationDialogProps {
  open: boolean;
}

export const SupabaseConfigurationDialog = memo<SupabaseConfigurationDialogProps>(({ open }) => {
  const theme = useStore(themeStore);
  const step = useStore(supabaseStore.supabaseProjectConfigurationStep);

  const [selectedProject, setSelectedProject] = useState<SupabaseProject | null>(null);
  const [organization, setOrganization] = useState<SupabaseOrganizationWithProjects | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFreePlan, setIsFreePlan] = useState(false);
  const [isReconnectingSupabase, setIsReconnectingSupabase] = useState(false);

  const activePolling = useRef<{ [key: string]: boolean }>({});

  function updateProjectStatus(updatedProject: SupabaseProject) {
    setOrganization((currentOrganization) => {
      if (!currentOrganization) {
        return null;
      }

      return {
        ...currentOrganization,
        projects: currentOrganization.projects.map((project) =>
          updatedProject.id === project.id ? updatedProject : project,
        ),
      };
    });
  }

  const pollProjectStatus = useCallback(async (project: SupabaseProject, endStatus: SupabaseProjectStatus) => {
    activePolling.current[project.id] = true;

    try {
      while (activePolling.current[project.id]) {
        const status = await getProjectStatus(project.id);

        if (!status) {
          continue;
        }

        if (ERROR_PROJECT_STATUS.includes(status) || status === endStatus) {
          setOrganization((currentOrganization) => {
            if (!currentOrganization) {
              return null;
            }

            return {
              ...currentOrganization,
              projects: currentOrganization.projects.map((proj) =>
                project.id === proj.id ? { ...project, status } : proj,
              ),
            };
          });
          break;
        }

        await wait(2000);
      }
    } catch (error) {
      console.error('Error polling project status:', error);
    } finally {
      delete activePolling.current[project.id];
    }
  }, []);

  useEffect(() => {
    async function fetchData() {
      if (open) {
        setIsLoading(true);

        try {
          const organizations = await getOrganizations();
          const currentOrganization = organizations.organizations[0];

          setOrganization(currentOrganization);

          if (currentOrganization) {
            /**
             * Projects can be pending status when it's being paused or restored,
             * we need to poll the status and update the project status.
             */
            startPollingPendingProjects(currentOrganization);

            const organizationInfo = await getOrganizationInfo(currentOrganization.id);
            setIsFreePlan(organizationInfo?.organization.plan === 'free');
          }
        } catch (error) {
          if (error instanceof Error && error.message.includes('FORBIDDEN')) {
            await handleForbiddenError();
          } else {
            toast.error('Failed to load Supabase projects');
          }
        } finally {
          setIsLoading(false);
        }
      }
    }

    function startPollingPendingProjects(organization: SupabaseOrganizationWithProjects) {
      organization.projects
        .filter((project) => PENDING_PROJECT_STATUS.includes(project.status))
        .forEach((project) => {
          activePolling.current[project.id] = true;
          pollProjectStatus(project, project.status === 'PAUSING' ? 'INACTIVE' : 'ACTIVE_HEALTHY');
        });
    }

    async function handleForbiddenError() {
      setIsReconnectingSupabase(true);

      try {
        await reconnectSupabase(async () => {
          await fetchData();
        });
      } catch (error) {
        console.error('Error reconnecting Supabase:', error);
      } finally {
        setIsReconnectingSupabase(false);
      }
    }

    fetchData();

    return () => {
      // clean up all active polling on unmount
      Object.keys(activePolling.current).forEach((key) => {
        activePolling.current[key] = false;
      });
    };
  }, [open, pollProjectStatus]);

  const handleSelectProject = (project: SupabaseProject) => {
    if (project.status === 'INACTIVE') {
      return;
    }

    if (ERROR_PROJECT_STATUS.includes(project.status)) {
      window.open(`https://supabase.com/dashboard/project/${project.id}`, '_blank');
      return;
    }

    setSelectedProject(project);
    supabaseStore.supabaseProjectConfigurationStep.set('connect-existing');
  };

  const closeDialog = () => {
    onClose(step);
  };

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      maxWidth={676}
      maxHeight="90vh"
      contentClassName="overflow-y-auto no-scrollbar"
    >
      <div
        className="absolute w-full h-28"
        style={{
          backgroundImage:
            theme === 'dark'
              ? 'linear-gradient(180deg, rgba(10, 75, 50, 0.00) 0%, rgba(24, 177, 117, 0.60) 100%)'
              : 'linear-gradient(180deg, rgba(10, 75, 50, 0.00) 0%, rgba(24, 177, 117, 0.20) 100%)',
        }}
      />
      <div className="absolute top-28 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-[20px] dark:bg-black bg-white border border-bolt-elements-borderColor flex items-center justify-center">
        <span className="i-logos:supabase-icon size-10" />
      </div>
      <div className="mt-44 mx-16 mb-6">
        {step === 'initial' && (
          <SelectProject
            organization={organization}
            isLoading={isLoading || isReconnectingSupabase}
            handleSelectProject={handleSelectProject}
            handleCreateNewProject={handleCreateNewProject}
            isFreePlan={isFreePlan}
            updateProjectStatus={updateProjectStatus}
            pollProjectStatus={pollProjectStatus}
          />
        )}
        {step === 'create-new' && <CreateNewProject closeDialog={closeDialog} organization={organization} />}
        {step === 'confirm-creation' && <ConfirmDatabaseCreation organization={organization} />}
        {step === 'connect-existing' && selectedProject && (
          <ConnectExistingProject closeDialog={closeDialog} project={selectedProject} />
        )}
      </div>
      {step !== 'initial' && (
        <button
          className="absolute top-[11px] left-[10px] text-xs px-2 py-1.5 bg-transparent flex items-center gap-1 rounded-md hover:bg-bolt-elements-item-backgroundActive"
          onClick={handleReturnToSelectProject}
          aria-label="Back to project selection"
        >
          <div className="i-ph:arrow-left size-4" />
          Back
        </button>
      )}
    </Dialog>
  );
});

interface DialogHeaderProps {
  title: string;
  description: ReactNode;
}

function DialogHeader({ title, description }: DialogHeaderProps) {
  return (
    <>
      <RadixDialog.Title className="text-lg text-center font-semibold mb-1">{title}</RadixDialog.Title>
      <RadixDialog.Description className="flex flex-col gap-4 mb-5 text-center text-bolt-elements-textSecondary leading-6">
        {description}
      </RadixDialog.Description>
    </>
  );
}

function OverflowBottomGradient({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative">
      {children}
      <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-gray-50 dark:from-gray-900 to-transparent" />
    </div>
  );
}
